import { useCallback, useState } from 'react'
import { useIntl, type IntlShape } from 'react-intl'

import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import SolidHeartIcon from 'LuxKit/Icons/solid/SolidHeartIcon'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { MAX_TRIP_NAME_CHARS } from 'tripPlanner/config'
import type { WishlistBookmarkPayload, WishlistSavedState, WishlistSummary } from '../types'
import { useCreateWishlist, useSaveWishlistOffer } from '../hooks'
import { getWishlistPath } from '../paths'
import useWishlistRegionCode from './useWishlistRegionCode'

export type WishlistModalResult =
  | { status: 'dismissed' }
  | { status: 'saved' }

interface Props {
  item: WishlistBookmarkPayload
  title: string
  imageIds: Array<string>
  subtitle?: string
  imageId?: string
  deeplinkUrl?: string
  locationName?: string
  suggestedWishlistName?: string
  latitude?: number
  longitude?: number
  initialSavedState?: WishlistSavedState
  customWishlists: Array<WishlistSummary>
}

const EMPTY_WISHLIST_IDS: Array<string> = []

function getSuggestedWishlistName(
  intl: IntlShape,
  suggestedWishlistName?: string,
  locationName?: string,
): string {
  const trimmedSuggestedWishlistName = suggestedWishlistName?.trim()
  const trimmedLocationName = locationName?.trim()

  if (trimmedSuggestedWishlistName) {
    return trimmedSuggestedWishlistName
  }

  if (!trimmedLocationName) {
    return ''
  }

  return intl.formatMessage(
    {
      id: 'HV6nJwCSVFUDB2jgYlBz',
      defaultMessage: '{locationName} Trip',
      description: 'Suggested wishlist name when creating a wishlist from an offer save modal',
    },
    { locationName: trimmedLocationName },
  )
}

function getInitialWishlistName(
  intl: IntlShape,
  suggestedWishlistName?: string,
  locationName?: string,
): string {
  const suggestedName = getSuggestedWishlistName(intl, suggestedWishlistName, locationName)

  return suggestedName.length <= MAX_TRIP_NAME_CHARS ? suggestedName : ''
}

export default function useSaveToWishlistFlow({
  item,
  title,
  imageIds,
  subtitle,
  imageId,
  deeplinkUrl,
  locationName,
  suggestedWishlistName,
  latitude,
  longitude,
  initialSavedState,
  customWishlists,
}: Props) {
  const intl = useIntl()
  const regionCode = useWishlistRegionCode()
  const modalElement = useModalElementContext<WishlistModalResult>()
  const { mutateAsync: saveWishlistOffer, isLoading: isSaving } = useSaveWishlistOffer()
  const { mutateAsync: createWishlist, isLoading: isCreatingWishlist } = useCreateWishlist()

  const [savedState, setSavedState] = useState<WishlistSavedState | undefined>(initialSavedState)
  const [newWishlistName, setNewWishlistName] = useState(() =>
    getInitialWishlistName(intl, suggestedWishlistName, locationName),
  )
  const [nameError, setNameError] = useState('')

  const selectedWishlistIds = savedState?.wishlistIds ?? EMPTY_WISHLIST_IDS
  const isSaved = selectedWishlistIds.length > 0
  const isMutating = isSaving || isCreatingWishlist
  const canSaveToWishlist = Boolean(regionCode)

  const onDismiss = useCallback(() => {
    modalElement.resolve({ status: isSaved ? 'saved' : 'dismissed' })
  }, [isSaved, modalElement])

  const showSavedSuccessSnackbar = useCallback(
    (wishlistId: string, wishlistName: string) => {
      showSnackbar(
        intl.formatMessage(
          {
            id: 'z5Vh-F1CAMng4kcByX9g',
            defaultMessage: 'Saved to "{wishlistName}"',
            description: 'Success snackbar shown after saving an offer to a wishlist',
          },
          { wishlistName },
        ),
        'neutral',
        {
          icon: <SolidHeartIcon colour="favourite" />,
          action: {
            label: intl.formatMessage({
              id: 'SJJr84BWgoSh4lwuZ5Ro',
              defaultMessage: 'View wishlist',
              description: 'Action label on the save success snackbar that navigates to the wishlist',
            }),
            to: getWishlistPath(regionCode, wishlistId),
          },
        },
      )
    },
    [intl, regionCode],
  )

  const showMissingRegionSnackbar = useCallback(() => {
    showSnackbar(
      intl.formatMessage({
        id: 'RCy6b7Mq-MZrxnMlpv2i',
        defaultMessage: 'We could not determine your region right now. Please refresh and try again.',
        description: 'Error snackbar shown when saving to a wishlist is blocked because the region is unavailable',
      }),
      'critical',
    )
  }, [intl])

  const saveToWishlist = useCallback(async(wishlistId: string) => {
    const wishlist = customWishlists.find((entry) => entry.id === wishlistId)

    if (!wishlist) {
      return
    }

    if (!regionCode) {
      showMissingRegionSnackbar()
      return
    }

    try {
      const result = await saveWishlistOffer({
        region: regionCode,
        wishlistIds: [wishlistId],
        item,
        title,
        imageIds,
        subtitle,
        imageId,
        deeplinkUrl,
        locationName,
        latitude,
        longitude,
      })

      setSavedState((currentState) =>
        currentState
          ? {
              ...currentState,
              savedOfferId: result.savedOfferId,
              fingerprint: result.fingerprint,
              wishlistIds: Array.from(
                new Set([...currentState.wishlistIds, ...result.wishlistIds]),
              ),
            }
          : result,
      )
      showSavedSuccessSnackbar(wishlist.id, wishlist.name)
      modalElement.resolve({ status: 'saved' })
    } catch {
      showSnackbar(
        intl.formatMessage({
          id: 'ClTj4g1nLPWel2mm4xhp',
          defaultMessage: 'Could not update this wishlist right now',
          description: 'Error message shown when wishlist membership updates fail',
        }),
        'critical',
      )
    }
  }, [
    customWishlists,
    deeplinkUrl,
    imageId,
    imageIds,
    intl,
    item,
    latitude,
    locationName,
    longitude,
    modalElement,
    regionCode,
    saveWishlistOffer,
    showMissingRegionSnackbar,
    showSavedSuccessSnackbar,
    subtitle,
    title,
  ])

  const createAndSave = useCallback(async() => {
    const trimmedName = newWishlistName.trim()

    if (!trimmedName) {
      setNameError(
        intl.formatMessage({
          id: 'fWv8HzVJazN-Er-fIcuG',
          defaultMessage: 'Enter a wishlist name',
          description: 'Validation message shown when wishlist name is empty',
        }),
      )
      return
    }

    if (!regionCode) {
      showMissingRegionSnackbar()
      return
    }

    try {
      const wishlist = await createWishlist(trimmedName)
      setNewWishlistName('')
      setNameError('')

      try {
        const result = await saveWishlistOffer({
          region: regionCode,
          wishlistIds: [wishlist.id],
          item,
          title,
          imageIds,
          subtitle,
          imageId,
          deeplinkUrl,
          locationName,
          latitude,
          longitude,
        })

        setSavedState((currentState) =>
          currentState
            ? {
                ...currentState,
                savedOfferId: result.savedOfferId,
                fingerprint: result.fingerprint,
                wishlistIds: Array.from(
                  new Set([...currentState.wishlistIds, ...result.wishlistIds]),
                ),
              }
            : result,
        )
      } catch {
        showSnackbar(
          intl.formatMessage({
            id: 'qJo9oJ60uOvm_o6biOEa',
            defaultMessage: 'Created the wishlist, but could not save this offer right now',
            description: 'Error message shown when wishlist creation succeeds but saving the current offer fails',
          }),
          'critical',
        )
        return
      }

      showSnackbar(
        intl.formatMessage(
          {
            id: 'kw1qvdSGGx7wLhEBtrEc',
            defaultMessage: 'Created and saved to "{wishlistName}"',
            description: 'Success snackbar shown after creating a wishlist from the save modal',
          },
          { wishlistName: wishlist.name },
        ),
        'neutral',
        {
          icon: <SolidHeartIcon colour="favourite" />,
          action: {
            label: intl.formatMessage({
              id: 'SJJr84BWgoSh4lwuZ5Ro',
              defaultMessage: 'View wishlist',
              description: 'Action label on the save success snackbar that navigates to the wishlist',
            }),
            to: getWishlistPath(regionCode, wishlist.id),
          },
        },
      )
      modalElement.resolve({ status: 'saved' })
    } catch {
      showSnackbar(
        intl.formatMessage({
          id: 'bGB0EPoj2RgQbAa0Lr9f',
          defaultMessage: 'Could not create a wishlist right now',
          description: 'Error message shown when wishlist creation from the save modal fails',
        }),
        'critical',
      )
    }
  }, [
    createWishlist,
    deeplinkUrl,
    imageId,
    imageIds,
    intl,
    item,
    latitude,
    locationName,
    longitude,
    modalElement,
    newWishlistName,
    regionCode,
    saveWishlistOffer,
    showMissingRegionSnackbar,
    subtitle,
    title,
  ])

  return {
    canSaveToWishlist,
    createAndSave,
    isCreatingWishlist,
    isMutating,
    isSaved,
    nameError,
    newWishlistName,
    onDismiss,
    saveToWishlist,
    setNameError,
    setNewWishlistName,
  }
}
